import React, { FC } from 'react';
import { WithTranslation } from 'react-i18next';
import DomainSensitiveLink from '../../../../../common/domain-sensitive-link/domain-sensitive-link';
import FontAwesomeIcon from '../../../../../ui/font-awesome-icon/font-awesome-icon';
import Icon from '../../../../../common/icon/icon';
import Preview from '../../../../../common/preview/preview';
import useIsMobile from '../../../../../hooks/use-is-mobile';
import { world } from '../../../../../languages';
import cn from '../../../../../utils/classnames';
import { hasMaterialAccess } from '../../../../../utils/material/material-helper';
import session from '../../../../../utils/session';
import getMaterialLink from '../../../../../utils/url/get-material-link';
import CartItemHeader from '../cart-item-header/cart-item-header';
import CartItemPrice from '../cart-item-price/cart-item-price';
import './cart-item.scss';

type CartItemProps = {
    index: number;
    internalPath: string;
    item: CartItem;
    onAddToFavorite: (item: CartItem, index: number) => void;
    onRemove: (item: CartItem, index: number) => void;
    t: WithTranslation['t'];
    className?: string;
    alertMessage?: string;
};

const CartItem: FC<CartItemProps> = (props) => {
    const { onAddToFavorite, onRemove, t, item, internalPath, index, className, alertMessage } = props;

    const mobile = useIsMobile(991);

    return (
        <div className={cn('cart-item-container', { 'with-alert': alertMessage })}>
            <div
                key={`cart-item-${item.material.id}`}
                className={cn(
                    `position-relative cart-item-optimized d-flex justify-content-start cart-item-${item.material.id}`,
                    className,
                    { [world]: world }
                )}
            >
                <DomainSensitiveLink className='thumbnail' href={getMaterialLink(item.material, internalPath)}>
                    <Preview
                        height={145}
                        item={item.material}
                        materialProps={{ internalPath, itemIndex: index }}
                        width={100}
                        shouldHideInteractiveBadge
                    />
                </DomainSensitiveLink>
                <div className='d-flex flex-column justify-content-between flex-1'>
                    <div>
                        <CartItemHeader item={item} onRemove={() => onRemove(item, index)} itemIndex={index} />
                        <div className='author-name mt-12'>{item.material.author.details.publicName}</div>
                    </div>
                    <div>
                        <div className='bottom-section d-flex align-items-end'>
                            {mobile && <CartItemPrice item={item} />}
                            <div className={cn('d-flex actions-block', { single: !session.isLoggedIn() })}>
                                {session.isLoggedIn() && !hasMaterialAccess(item.material) && (
                                    <>
                                        <div>
                                            {!mobile && (
                                                <span
                                                    className='add-to-favorites'
                                                    onClick={() => onAddToFavorite(item, index)}
                                                >
                                                    <span className='gray-text'>
                                                        {t('common.materialBlock.addToFavorites')}
                                                    </span>
                                                </span>
                                            )}
                                        </div>
                                        {!mobile && <div className='spliter gray-text ml-1 mr-1'>|</div>}
                                        {mobile && (
                                            <div
                                                onClick={() => onAddToFavorite(item, index)}
                                                className='d-flex align-items-center justify-content-between fav-block'
                                            >
                                                <Icon
                                                    className='mr-1'
                                                    width={32}
                                                    height={32}
                                                    size='sm'
                                                    onClick={() => onAddToFavorite(item, index)}
                                                    name='favorite'
                                                />
                                                <span className='tsc-body-highlighted'>
                                                    {t(
                                                        mobile
                                                            ? 'common.materialBlock.addToFavorites'
                                                            : 'cart.cartItems.addToFavorites'
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}
                                <div>
                                    {!mobile && (
                                        <span className='gray-text remove-item' onClick={() => onRemove(item, index)}>
                                            {t('cart.cartItems.remove')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {alertMessage && (
                <div className='alert alert-warning'>
                    <FontAwesomeIcon height={19} name='warning-triangle' theme='yellow' />

                    <span>{alertMessage}</span>
                </div>
            )}
        </div>
    );
};

export default CartItem;
